/**
 * Dashboard Hub Route (Dashboards feature)
 *
 * The /dashboard page is PURELY a hub for the tenant's buildable dashboards. It
 * now presents the tenant's saved dashboards the SAME way the Clients and Reports
 * list pages present their items — a responsive CARD GRID (cloning the
 * Client/Report card language: rounded-[22px], 0.5px borders, shadow-xs, the
 * avatar/name/meta layout) under a clean toolbar with a prominent "New Dashboard"
 * primary button. There is NO redundant in-content "Dashboard" heading — the page
 * title lives in the shared header breadcrumb (`ReportBreadcrumb`), exactly like
 * Clients/Reports.
 *
 *   - ZERO saved dashboards (`/api/dashboards` empty) → a centered, friendly
 *     EMPTY STATE with a single prominent "Create your first dashboard" action,
 *     consistent with the Clients/Reports empty states.
 *
 *   - ONE OR MORE saved dashboards → the CARD GRID. Clicking a card OPENS that
 *     dashboard: it is rendered READ-ONLY below the grid (loaded via
 *     `useDashboard`, its `canvasLayout` widgets + bound `clientId` fed into the
 *     report `ReportCanvas` with LIVE data via `useReportData` — the same
 *     read-only preview path the report builder uses) with a "Back to dashboards"
 *     link. Each card also exposes Edit (→ /dashboard/$id/edit) and Delete
 *     (confirm) via a three-dot menu, matching the Client/Report cards.
 *
 * "New Dashboard" / "Create your first dashboard" navigate to the dedicated
 * `/dashboard/new` "Choose Client" SCREEN (mirrors the report generate flow's
 * first step), which handles client selection + the create methods.
 *
 * The opened dashboard id persists to `localStorage["dashboard-selected"]` so the
 * choice restores on return; a stale id is ignored (grid stays the default view).
 */

import { useState, useCallback, useMemo, useEffect } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { PageSpinner } from "../../../components/PageSpinner";
import { AnimatePresence, motion } from "motion/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  SquaresFour,
  ChartPieSlice,
  PencilSimple,
  ShareNetwork,
  Trash,
  ArrowLeft,
  Eye,
  DotsThreeVertical,
  CalendarDots,
} from "@phosphor-icons/react";
import {
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@provenleap/ui";
import { ReportBreadcrumb } from "../../../components/reports/ReportBreadcrumb";
import { ClientAvatar } from "../../../components/clients/ClientAvatar";
import { useToast } from "../../../hooks/useToast";
import { Toast } from "../../../components/clients/Toast";
import { apiGet, apiDelete } from "../../../lib/apiClient";
import { fromCanvasLayout } from "../../../lib/dashboardLayout";
import { useDashboard } from "../../../hooks/useDashboard";
import { useReportData } from "../../../hooks/useReportData";
import { useDateRange } from "../../../hooks/useDateRange";
import { useClientIntegrations } from "../../../hooks/useClientIntegrations";
import { isClientConnected } from "../../../lib/reportClientSource";
import { generateMockData } from "../../../lib/mockWidgetData";
import { widgetHasDataBinding } from "../../../lib/widgetCompatibility";
import { DashboardCanvas } from "../../../components/dashboards/DashboardCanvas";
import { DashboardShareDialog } from "../../../components/dashboards/DashboardShareDialog";
import { DashboardTableView } from "../../../components/dashboards/DashboardTableView";
import {
  DASHBOARD_HIDEABLE_COLUMNS,
  DASHBOARD_STATUS_STYLES,
  formatDashboardRange,
  type DashboardSortOption,
} from "../../../components/dashboards/dashboardColumns";
import {
  DashboardFilterToolbar,
  type DashboardStatus,
} from "../../../components/dashboards/DashboardFilterToolbar";
import { useHiddenColumns } from "../../../components/shared/columnVisibility";
import type { DateRangePresetKey } from "../../../lib/dateRangeUtils";
import type { WidgetConfig } from "../../../lib/templateUtils";
import type { WidgetData } from "../../../lib/widgetData";

export const Route = createFileRoute("/_authenticated/dashboard/")({
  component: DashboardHubPage,
});


// ── Selection model ──────────────────────────────────────────────────────────

/** localStorage key persisting which saved dashboard the hub last opened. */
const SELECTED_KEY = "dashboard-selected";

// ── View-mode model (grid ↔ table) ───────────────────────────────────────────

/** Grid (card) vs table view for the hub, mirroring Clients/Reports. */
type DashboardViewMode = "grid" | "table";

/** localStorage key persisting the hub's grid/table choice (default "grid"). */
const VIEW_MODE_KEY = "dashboardList_viewMode";

function readPersistedViewMode(): DashboardViewMode {
  try {
    return window.localStorage.getItem(VIEW_MODE_KEY) === "table"
      ? "table"
      : "grid";
  } catch {
    return "grid";
  }
}

interface DashboardListItem {
  id: string;
  // Story 40-7: the LIST endpoint LEFT-joins clients so roll-up dashboards
  // (kind="rollup", no bound client) now appear here with a NULL client — the
  // consumer must treat clientId/clientName as nullable.
  clientId: string | null;
  clientName: string | null;
  name: string;
  status: string;
  dateRangeStart: string | null;
  dateRangeEnd: string | null;
  dateRangePreset: string | null;
  // Story 40-7 roll-up discriminator (server LIST returns these). Absent ⇒ a
  // single-client dashboard ("client").
  kind?: "client" | "rollup";
  rollupScope?: "brand" | "portfolio" | null;
  brandId?: string | null;
  createdBy: string | null;
  createdAt: number;
  updatedAt: number;
}

/** Display label for a roll-up dashboard's "client" slot (brand vs portfolio). */
function rollupLabel(item: {
  kind?: "client" | "rollup";
  rollupScope?: "brand" | "portfolio" | null;
}): string {
  return item.rollupScope === "brand" ? "Brand roll-up" : "Portfolio roll-up";
}

function readPersistedSelection(): string | null {
  try {
    return window.localStorage.getItem(SELECTED_KEY);
  } catch {
    return null;
  }
}

function persistSelection(value: string): void {
  try {
    window.localStorage.setItem(SELECTED_KEY, value);
  } catch {
    // localStorage unavailable (private mode / SSR) — non-fatal.
  }
}

function clearPersistedSelection(): void {
  try {
    window.localStorage.removeItem(SELECTED_KEY);
  } catch {
    // non-fatal
  }
}

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// ── Hub page ────────────────────────────────────────────────────────────────

function DashboardHubPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast, showToast } = useToast();

  // Which saved dashboard the hub currently has OPEN (read-only). `null` shows
  // the card grid. Restored from localStorage so the open dashboard returns.
  const [opened, setOpened] = useState<string | null>(() =>
    readPersistedSelection(),
  );

  // Grid (cards) vs table view for the hub list — persisted so the choice
  // restores on return, exactly like Clients ("clientList_viewMode") and Reports.
  const [viewMode, setViewMode] = useState<DashboardViewMode>(() =>
    readPersistedViewMode(),
  );

  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_MODE_KEY, viewMode);
    } catch {
      // localStorage unavailable (private mode / SSR) — non-fatal.
    }
  }, [viewMode]);

  // Sort + column-visibility state for the hub list (mirrors Reports). The sort
  // comparator is the reports' switch MINUS format; hidden columns persist to
  // localStorage under the dashboard-specific key.
  const [sortBy, setSortBy] = useState<DashboardSortOption>("created_desc");
  const { hiddenColumns, toggleColumn } = useHiddenColumns(
    "dashboardTable_hiddenColumns",
    DASHBOARD_HIDEABLE_COLUMNS,
  );

  // ── Search + filter state (mirrors the reports list toolbar) ──────────────
  // Search matches the dashboard name OR its client name; the client/status
  // filters narrow the list. All applied in `dashboardList` BEFORE sorting.
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<DashboardStatus[]>(
    [],
  );

  // Card-grid delete target (the per-card menu's Delete) — confirmed via a
  // shared dialog at the hub level, matching the Reports list delete flow.
  const [deleteTarget, setDeleteTarget] = useState<DashboardListItem | null>(
    null,
  );
  // Card/table Share target (the per-row menu's Share) — opens the shared
  // DashboardShareDialog at the hub level (same entry point as the editor).
  const [shareTarget, setShareTarget] = useState<DashboardListItem | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiDelete(`/api/dashboards/${id}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Delete failed" }));
        throw new Error(err.detail ?? "Delete failed");
      }
    },
    onSuccess: (_data, id) => {
      void queryClient.removeQueries({ queryKey: ["dashboard", id] });
      void queryClient.invalidateQueries({ queryKey: ["dashboards"] });
      setDeleteTarget(null);
      showToast("success", "Dashboard deleted.");
    },
  });

  // Saved dashboards drive the card grid (name + client + timestamps).
  // `isLoading`/`isError` are surfaced so a refresh shows a spinner (not the
  // "create your first dashboard" empty state) until the fetch resolves.
  const {
    data: dashboards,
    isLoading: dashboardsLoading,
    isError: dashboardsError,
  } = useQuery<DashboardListItem[]>({
    queryKey: ["dashboards"],
    queryFn: async () => {
      const res = await apiGet("/api/dashboards");
      if (!res.ok) throw new Error("Failed to load dashboards");
      const json = await res.json();
      return json.data as DashboardListItem[];
    },
  });

  // Are there any clients? Gates "New Dashboard" (a dashboard is client-bound).
  const { data: hasClients } = useQuery<boolean>({
    queryKey: ["clients-exist"],
    queryFn: async () => {
      const res = await apiGet("/api/clients");
      if (!res.ok) return false;
      const json = await res.json();
      return Array.isArray(json.data) && json.data.length > 0;
    },
  });

  // A persisted "opened" id that is no longer in the list is stale — drop it so
  // the grid (not a phantom view) is shown. Valid open ids are kept.
  useEffect(() => {
    if (!dashboards || opened === null) return;
    if (!dashboards.some((d) => d.id === opened)) {
      setOpened(null);
      clearPersistedSelection();
    }
  }, [dashboards, opened]);

  const handleOpen = useCallback((id: string) => {
    setOpened(id);
    persistSelection(id);
  }, []);

  const handleBackToGrid = useCallback(() => {
    setOpened(null);
    clearPersistedSelection();
  }, []);

  // ── New Dashboard: navigate to the dedicated "Choose Client" screen ─────
  const handleCreate = useCallback(() => {
    navigate({ to: "/dashboard/new" });
  }, [navigate]);

  const hasDashboards = (dashboards?.length ?? 0) > 0;

  // Client-filter options derived from the saved dashboards themselves (unique
  // clientId → clientName), sorted by name — mirrors how the reports toolbar
  // populates its client multi-select, but sourced from the list in hand.
  const clientOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const d of dashboards ?? []) {
      // Skip roll-up rows (null clientId/clientName) — they have no single client
      // to offer in the client filter (Story 40-7 LEFT-join nullability).
      if (d.clientId && d.clientName && !seen.has(d.clientId)) {
        seen.set(d.clientId, d.clientName);
      }
    }
    return [...seen.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [dashboards]);

  // Filtered + sorted list driving both the grid and the table. FILTER first
  // (search on name|clientName, client multi-select, status multi-select),
  // exactly mirroring the reports route's pipeline, THEN sort. The comparator
  // mirrors the reports' switch (name/client/status on string localeCompare,
  // date_range on dateRangeStart localeCompare, created/updated on numeric ts)
  // MINUS format. Null date-range starts sort as empty strings so they cluster.
  const dashboardList = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list = q
      ? (dashboards ?? []).filter(
          (d) =>
            d.name.toLowerCase().includes(q) ||
            (d.clientName ?? "").toLowerCase().includes(q),
        )
      : (dashboards ?? []).slice();

    if (selectedClientIds.length > 0) {
      list = list.filter((d) => selectedClientIds.includes(d.clientId ?? ""));
    }

    if (selectedStatuses.length > 0) {
      list = list.filter((d) =>
        selectedStatuses.includes(d.status as DashboardStatus),
      );
    }

    return [...list].sort((a, b) => {
      switch (sortBy) {
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "client_asc":
          return (a.clientName ?? "").localeCompare(b.clientName ?? "");
        case "client_desc":
          return (b.clientName ?? "").localeCompare(a.clientName ?? "");
        case "status_asc":
          return a.status.localeCompare(b.status);
        case "status_desc":
          return b.status.localeCompare(a.status);
        case "date_range_asc":
          return (a.dateRangeStart ?? "").localeCompare(b.dateRangeStart ?? "");
        case "date_range_desc":
          return (b.dateRangeStart ?? "").localeCompare(a.dateRangeStart ?? "");
        case "created_asc":
          return a.createdAt - b.createdAt;
        case "created_desc":
          return b.createdAt - a.createdAt;
        case "updated_asc":
          return a.updatedAt - b.updatedAt;
        case "updated_desc":
          return b.updatedAt - a.updatedAt;
        default:
          return 0;
      }
    });
  }, [
    dashboards,
    searchQuery,
    selectedClientIds,
    selectedStatuses,
    sortBy,
  ]);

  // True iff the tenant has dashboards but the active filters/search exclude
  // ALL of them — distinct from the no-dashboards-at-all empty state, so the
  // toolbar stays visible and the user can clear the filters (mirrors reports).
  const isFilteredEmpty = hasDashboards && dashboardList.length === 0;

  // Only treat `opened` as active once it is confirmed present in the list — a
  // stale persisted id (cleared by the effect above) must not flash a view.
  const activeOpenId =
    opened && dashboardList.some((d) => d.id === opened) ? opened : null;

  return (
    <div className="p-6 max-w-[1800px] mx-auto">
      <ReportBreadcrumb items={[{ label: "Dashboard" }]} />
      <div className="mb-6" />

      {dashboardsLoading ? (
        /* Refresh / first load — spinner instead of flashing the empty state. */
        <PageSpinner />
      ) : dashboardsError ? (
        <div
          className="flex flex-col items-center justify-center py-24 text-center"
          data-testid="dashboard-error-state"
        >
          <h2 className="text-lg font-semibold text-foreground">
            Couldn’t load your dashboards
          </h2>
          <p className="mt-2 max-w-md text-[13px] text-muted-foreground">
            Something went wrong fetching your dashboards. Refresh the page to
            try again.
          </p>
        </div>
      ) : !hasDashboards ? (
        <DashboardEmptyState
          disabled={hasClients === false}
          disabledReason={
            hasClients === false
              ? "Add a client first to create dashboards"
              : undefined
          }
          onCreate={handleCreate}
        />
      ) : activeOpenId ? (
        /* An open dashboard — read-only canvas with a back link. */
        <SavedDashboardView
          key={activeOpenId}
          dashboardId={activeOpenId}
          onBack={handleBackToGrid}
          onEdit={() =>
            navigate({
              to: "/dashboard/$dashboardId/edit",
              params: { dashboardId: activeOpenId },
            })
          }
          onDeleted={() => {
            setOpened(null);
            clearPersistedSelection();
            void queryClient.invalidateQueries({ queryKey: ["dashboards"] });
            showToast("success", "Dashboard deleted.");
          }}
        />
      ) : (
        /* The hub list — card grid OR table, toggled by the view switcher. */
        <>
          {/* Filter toolbar — search + Filters dropdown + Columns (table only) +
              view switcher + New Dashboard, cloning the reports list toolbar. */}
          <DashboardFilterToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            availableClients={clientOptions}
            selectedClientIds={selectedClientIds}
            onClientFilterChange={setSelectedClientIds}
            selectedStatuses={selectedStatuses}
            onStatusFilterChange={setSelectedStatuses}
            view={viewMode}
            onViewChange={setViewMode}
            onNewDashboard={handleCreate}
            newDashboardDisabled={hasClients === false}
            hiddenColumns={hiddenColumns}
            onToggleColumn={toggleColumn}
            showColumnsControl={viewMode === "table"}
          />

          {isFilteredEmpty ? (
            /* Filters/search excluded every dashboard — distinct from the
               no-dashboards-at-all empty state (the toolbar stays visible). */
            <div
              className="flex flex-col items-center justify-center py-24 text-center"
              data-testid="dashboard-filtered-empty-state"
            >
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-muted/40 text-muted-foreground">
                <SquaresFour size={28} weight="duotone" />
              </div>
              <h2 className="mt-5 text-lg font-semibold text-foreground">
                No dashboards match your filters
              </h2>
              <p className="mt-2 max-w-md text-[13px] text-muted-foreground">
                Try a different search term, or clear the client and status
                filters to see all your dashboards.
              </p>
            </div>
          ) : (
          <AnimatePresence mode="wait">
            {viewMode === "grid" ? (
              <motion.div
                key="dashboard-grid"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <DashboardCardGrid
                  dashboards={dashboardList}
                  onOpen={handleOpen}
                  onEdit={(id) =>
                    navigate({
                      to: "/dashboard/$dashboardId/edit",
                      params: { dashboardId: id },
                    })
                  }
                  onShare={(dashboard) => setShareTarget(dashboard)}
                  onDelete={(dashboard) => {
                    deleteMutation.reset();
                    setDeleteTarget(dashboard);
                  }}
                />
              </motion.div>
            ) : (
              <motion.div
                key="dashboard-table"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2, ease: "easeOut" }}
              >
                <DashboardTableView
                  dashboards={dashboardList}
                  onOpen={handleOpen}
                  onEdit={(id) =>
                    navigate({
                      to: "/dashboard/$dashboardId/edit",
                      params: { dashboardId: id },
                    })
                  }
                  onShare={(dashboard) => setShareTarget(dashboard)}
                  onDelete={(dashboard) => {
                    deleteMutation.reset();
                    setDeleteTarget(dashboard);
                  }}
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  hiddenColumns={hiddenColumns}
                  onHideColumn={toggleColumn}
                />
              </motion.div>
            )}
          </AnimatePresence>
          )}
        </>
      )}

      {/* Card-grid delete confirmation (shared dialog). */}
      <DeleteDashboardDialog
        name={deleteTarget?.name ?? ""}
        open={!!deleteTarget}
        onOpenChange={(o) => {
          if (!o && !deleteMutation.isPending) setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
        }}
        isPending={deleteMutation.isPending}
        error={
          deleteMutation.error instanceof Error
            ? deleteMutation.error.message
            : null
        }
      />

      {/* Card/table Share dialog (same entry point as the editor's Share button).
          Single-client dashboards only — a roll-up has no bound clientId to scope
          its share links (the roll-up share surface is owned by Story 40-8). */}
      {shareTarget && shareTarget.clientId && (
        <DashboardShareDialog
          dashboardId={shareTarget.id}
          clientId={shareTarget.clientId}
          open={!!shareTarget}
          onOpenChange={(o) => {
            if (!o) setShareTarget(null);
          }}
          showToast={showToast}
        />
      )}

      <Toast toast={toast} />
    </div>
  );
}

// ── Card grid (clones the Client/Report card language) ───────────────────────

/**
 * Saved-dashboard CARD GRID — a 3-column responsive grid of rounded-[22px] cards
 * with 0.5px borders and a shadow-xs hover lift, matching ClientCardGrid /
 * ReportCardGrid exactly. Each card: dashboard name, client (ClientAvatar +
 * name), Created/Updated meta, and a three-dot menu (View / Edit / Delete).
 * Clicking the card body OPENS the read-only view.
 */
function DashboardCardGrid({
  dashboards,
  onOpen,
  onEdit,
  onShare,
  onDelete,
}: {
  dashboards: DashboardListItem[];
  onOpen: (id: string) => void;
  onEdit: (id: string) => void;
  onShare: (dashboard: DashboardListItem) => void;
  onDelete: (dashboard: DashboardListItem) => void;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {dashboards.map((dashboard) => {
        const statusStyle = DASHBOARD_STATUS_STYLES[dashboard.status];
        return (
        <Card
          key={dashboard.id}
          data-testid="dashboard-card"
          className="shadow-xs hover:shadow-sm transition-shadow cursor-pointer !rounded-[22px]"
          style={{ border: "0.5px solid var(--hairline)" }}
          onClick={() => onOpen(dashboard.id)}
          role="link"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onOpen(dashboard.id);
            }
          }}
        >
          <CardContent className="p-0">
            {/* Top: section icon + dashboard name + status pill */}
            <div className="flex items-center gap-3 px-4 pt-4 pb-3">
              <div className="flex flex-1 items-center gap-2 min-w-0">
                <h3 className="text-[14px] font-medium truncate min-w-0">
                  {dashboard.name}
                </h3>
              </div>
              {statusStyle && (
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium shrink-0 ${statusStyle.className}`}
                >
                  {statusStyle.label}
                </span>
              )}
            </div>

            {/* Divider */}
            <div
              className="mx-4"
              style={{ borderTop: "0.5px solid hsl(var(--input) / 0.5)" }}
            />

            {/* Body: client + date range + Created/Updated meta + actions */}
            <div className="pt-3 pb-3 px-4 space-y-3">
              {(() => {
                // Story 40-7: a roll-up dashboard has no bound client (clientName
                // null) — surface a brand/portfolio roll-up label in the client
                // slot instead. Single-client rows render byte-identically.
                const isRollup = dashboard.kind === "rollup";
                const clientLabel = isRollup
                  ? rollupLabel(dashboard)
                  : (dashboard.clientName ?? "");
                return (
                  <div className="flex items-center gap-2">
                    <ClientAvatar name={clientLabel} logoUrl={null} size="sm" />
                    <span className="text-[13px] text-foreground truncate">
                      {clientLabel}
                    </span>
                  </div>
                );
              })()}

              <div className="flex items-center gap-1.5 text-[13px] text-foreground">
                <CalendarDots size={18} />
                {formatDashboardRange(dashboard)}
              </div>

              <div className="flex items-center gap-3 text-[13px] text-foreground -mt-1">
                <span>Created {formatTimestamp(dashboard.createdAt)}</span>
                <span
                  className="w-px h-3.5"
                  style={{ background: "hsl(var(--input) / 0.3)" }}
                />
                <span>Updated {formatTimestamp(dashboard.updatedAt)}</span>
                <div
                  className="ml-auto"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="min-h-8 min-w-8 cursor-pointer rounded-lg border border-transparent hover:bg-background hover:border-input transition-all"
                        aria-label={`Actions for ${dashboard.name}`}
                        data-testid={`dashboard-card-menu-${dashboard.id}`}
                      >
                        <DotsThreeVertical size={22} weight="bold" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="end"
                      className="rounded-xl p-2 shadow-md"
                      style={{ border: "0.5px solid var(--hairline)" }}
                    >
                      <DropdownMenuItem
                        className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                        onClick={() => onOpen(dashboard.id)}
                      >
                        <Eye size={14} className="mr-1" />
                        View
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                        onClick={() => onEdit(dashboard.id)}
                      >
                        <PencilSimple size={14} className="mr-1" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="min-h-11 text-[13px] cursor-pointer rounded-lg"
                        onClick={() => onShare(dashboard)}
                        data-testid={`dashboard-card-share-${dashboard.id}`}
                      >
                        <ShareNetwork size={14} className="mr-1" />
                        Share
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="min-h-11 text-[13px] text-destructive cursor-pointer rounded-lg hover:!bg-destructive/10 focus:!bg-destructive/10 focus:!text-destructive focus:!border-destructive/30"
                        onClick={() => onDelete(dashboard)}
                      >
                        <Trash size={14} className="mr-1" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        );
      })}
    </div>
  );
}

// ── Empty state ─────────────────────────────────────────────────────────────

/** Centered, friendly empty state shown when the tenant has no dashboards. */
function DashboardEmptyState({
  disabled,
  disabledReason,
  onCreate,
}: {
  disabled: boolean;
  disabledReason?: string;
  onCreate: () => void;
}) {
  return (
    <div
      className="flex flex-col items-center justify-center py-28 text-center"
      data-testid="dashboard-empty-state"
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted/40 text-muted-foreground">
        <SquaresFour size={32} weight="duotone" />
      </div>
      <h1 className="mt-6 text-2xl font-bold text-neutral-900">
        No dashboards yet
      </h1>
      <p className="mt-2 max-w-md text-[13px] text-muted-foreground">
        Build a live dashboard for any client — drag in KPIs, charts and tables,
        then share it read-only. Start with your first one.
      </p>
      <Button
        variant="brand"
        className="mt-6 rounded-2xl text-[13px] cursor-pointer"
        disabled={disabled}
        onClick={onCreate}
        data-testid="create-first-dashboard-button"
        title={disabledReason}
      >
        <ChartPieSlice size={16} className="mr-1" />
        Create your first dashboard
      </Button>
    </div>
  );
}

// ── Saved-dashboard read-only render ────────────────────────────────────────

/**
 * Renders a saved dashboard's canvas READ-ONLY with live data. Loads the row via
 * `useDashboard`, derives its bound `clientId`, fetches per-widget data via
 * `useReportData` (the report builder's live-data path), and feeds the widgets +
 * data into the report `ReportCanvas` with `editable={false}` (the same read-only
 * preview pattern as `ReportPreviewPanel`). A "Back to dashboards" link returns
 * to the grid; Edit jumps to the full editor; delete removes the row.
 */
function SavedDashboardView({
  dashboardId,
  onBack,
  onEdit,
  onDeleted,
}: {
  dashboardId: string;
  onBack: () => void;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dateRange = useDateRange();
  const { selectPreset, hydrate } = dateRange;

  const {
    data: dashboard,
    isLoading,
    isError,
  } = useDashboard(dashboardId);

  // Story 40-7: `clientId` is now nullable on the loaded row (null for a roll-up
  // dashboard). Coalesce null → undefined so the single-client preview queries
  // idle exactly as for an unloaded row (a roll-up preview falls back to mock —
  // 40-8 owns the roll-up preview surface).
  const clientId = dashboard?.clientId ?? undefined;

  // Widgets parsed defensively from the stored canvas blob.
  const widgets = useMemo<WidgetConfig[]>(
    () => (dashboard ? fromCanvasLayout(dashboard.canvasLayout) : []),
    [dashboard],
  );

  // Seed the date range from the saved dashboard so the read-only canvas fetches
  // live data over the SAME window the dashboard was configured for: a non-"custom"
  // preset re-derives the window via `selectPreset`; otherwise the persisted
  // start/end are restored VERBATIM via `hydrate`. (Mirrors the report editor's
  // saved-range seeding.) Effect keyed on the row's date-range fields.
  const dashboardPreset = dashboard?.dateRangePreset ?? null;
  const dashboardStart = dashboard?.dateRangeStart ?? null;
  const dashboardEnd = dashboard?.dateRangeEnd ?? null;
  useEffect(() => {
    if (dashboardPreset && dashboardPreset !== "custom") {
      selectPreset(dashboardPreset as DateRangePresetKey);
    } else if (dashboardStart && dashboardEnd) {
      hydrate({ start: dashboardStart, end: dashboardEnd });
    }
  }, [dashboardPreset, dashboardStart, dashboardEnd, selectPreset, hydrate]);

  // Client name for the header label.
  const { data: client } = useQuery<{ id: string; name: string }>({
    queryKey: ["clients", clientId],
    queryFn: async () => {
      const res = await apiGet(`/api/clients/${clientId}`);
      if (!res.ok) throw new Error("Failed to load client");
      const json = await res.json();
      return json.data;
    },
    enabled: !!clientId,
  });

  // ── Live ↔ mock data (mirrors the report editor's CanvasEditor merge) ──
  // The bug this fixes: a saved dashboard with an UNCONFIGURED widget (e.g. a
  // fresh KPI with an empty metricKey) used to fire `/api/reports/populate`
  // unconditionally with the raw clientId, the server rejected the empty metric
  // key with a 400 ("Unknown metric keys: ."), and `useReportData` returned a
  // per-widget ERROR envelope carrying that 400 text — which the canvas rendered
  // as the widget's content. The report editor never hits this because it (a)
  // only fires the live populate when the client is CONNECTED and (b) falls back
  // to sample/mock data on a whole-request failure. We replicate both here.
  //
  // Connected = an effective clientId AND ≥1 connected platform (the SINGLE
  // connected-decision source `isClientConnected`, same as the editor). When the
  // client is unconnected we pass `undefined` as the clientId so the hook stays
  // idle and NEVER fires a populate that would 400/all-miss; the canvas shows
  // pure mock data instead.
  const { connectedPlatforms } = useClientIntegrations(clientId);
  const isConnected = isClientConnected(clientId, connectedPlatforms);

  const {
    widgetData: liveWidgetData,
    isError: liveRequestFailed,
  } = useReportData(isConnected ? clientId : undefined, widgets, {
    start: dateRange.state.start,
    end: dateRange.state.end,
  });

  // Sample/mock data for the unconnected path AND the per-widget fallback.
  const mockData = useMemo(() => generateMockData(widgets), [widgets]);

  // The ONE merge decision (identical shape to CanvasEditor.canvasData):
  //   - unconnected client OR whole-request 400/non-200 → all mock (the canvas
  //     stays populated with sample numbers instead of showing the error text).
  //   - connected client → live data, but any widget that is UNBOUND (e.g. a
  //     fresh KPI with no metric key) or whose live envelope ERRORED falls back
  //     to its mock so it previews sample data rather than a red error message.
  const widgetData = useMemo<Record<string, WidgetData>>(() => {
    if (!isConnected || liveRequestFailed) return mockData;
    const merged: Record<string, WidgetData> = { ...liveWidgetData };
    for (const widget of widgets) {
      const mock = mockData[widget.id];
      if (!mock) continue;
      const live = liveWidgetData[widget.id];
      const unbound = !widgetHasDataBinding(widget.config as Record<string, unknown>);
      const liveErrored = !!live?.error;
      if (unbound || liveErrored) merged[widget.id] = mock;
    }
    return merged;
  }, [isConnected, liveRequestFailed, liveWidgetData, mockData, widgets]);

  // ── Delete ───────────────────────────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiDelete(`/api/dashboards/${dashboardId}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Delete failed" }));
        throw new Error(err.detail ?? "Delete failed");
      }
    },
    onSuccess: () => {
      void queryClient.removeQueries({ queryKey: ["dashboard", dashboardId] });
      setConfirmDelete(false);
      onDeleted();
    },
  });

  // ── Back-to-grid link — shared across loading/error/loaded states. ─────
  const backLink = (
    <button
      type="button"
      onClick={onBack}
      className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
      data-testid="back-to-dashboards"
    >
      <ArrowLeft size={15} />
      Back to dashboards
    </button>
  );

  if (isLoading) {
    return (
      <div data-testid="saved-dashboard-view">
        <div className="mb-4">{backLink}</div>
        <div
          className="flex items-center justify-center py-24 text-[13px] text-neutral-400"
          data-testid="saved-dashboard-loading"
        >
          Loading dashboard…
        </div>
      </div>
    );
  }

  if (isError || !dashboard) {
    return (
      <div data-testid="saved-dashboard-view">
        <div className="mb-4">{backLink}</div>
        <div
          className="rounded-2xl border-[0.5px] border-destructive bg-destructive/5 p-6 text-center"
          role="alert"
          data-testid="saved-dashboard-error"
        >
          <p className="text-sm text-destructive">
            Couldn’t load this dashboard. It may have been deleted, or you may not
            have access to it.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="saved-dashboard-view">
      {/* Header row — back link + name/client + Edit / Delete actions. */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1.5 min-w-0">
          {backLink}
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-[17px] font-semibold text-foreground truncate">
              {dashboard.name}
            </h1>
            {(() => {
              const statusStyle = DASHBOARD_STATUS_STYLES[dashboard.status];
              return statusStyle ? (
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium shrink-0 ${statusStyle.className}`}
                  data-testid="saved-dashboard-status"
                >
                  {statusStyle.label}
                </span>
              ) : null;
            })()}
            {client?.name && (
              <span className="text-[13px] text-muted-foreground truncate">
                · {client.name}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            className="rounded-2xl text-[13px] cursor-pointer"
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={onEdit}
            data-testid="edit-dashboard-button"
          >
            <PencilSimple size={15} className="mr-1.5" />
            Edit
          </Button>
          <Button
            variant="outline"
            className="rounded-2xl text-[13px] text-destructive cursor-pointer"
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={() => {
              deleteMutation.reset();
              setConfirmDelete(true);
            }}
            data-testid="delete-dashboard-button"
          >
            <Trash size={15} className="mr-1.5" />
            Delete
          </Button>
        </div>
      </div>

      {/* Read-only canvas — the report ReportCanvas (imported, not modified). */}
      {widgets.length === 0 ? (
        <div
          className="rounded-2xl border-[0.5px] border-input bg-muted/20 py-24 text-center"
          data-testid="saved-dashboard-empty"
        >
          <p className="text-sm text-muted-foreground">
            This dashboard is empty.
          </p>
          <button
            type="button"
            onClick={onEdit}
            className="mt-3 inline-flex items-center px-5 py-2 rounded-2xl bg-primary text-white text-[13px] font-medium hover:bg-primary/90 transition-colors cursor-pointer"
          >
            <PencilSimple size={15} className="mr-1.5" />
            Add widgets
          </button>
        </div>
      ) : (
        // Full-width, continuous web surface (NOT a centered A4 paper sheet).
        <div className="w-full">
          <DashboardCanvas
            widgets={widgets}
            widgetData={widgetData}
            editable={false}
            format="document"
            selectedClientName={client?.name}
          />
        </div>
      )}

      {/* Delete confirmation. */}
      <DeleteDashboardDialog
        name={dashboard.name}
        open={confirmDelete}
        onOpenChange={(o) => {
          if (!o && !deleteMutation.isPending) setConfirmDelete(false);
        }}
        onConfirm={() => deleteMutation.mutate()}
        isPending={deleteMutation.isPending}
        error={
          deleteMutation.error instanceof Error
            ? deleteMutation.error.message
            : null
        }
      />
    </div>
  );
}

// ── Delete confirmation dialog ──────────────────────────────────────────────

function DeleteDashboardDialog({
  name,
  open,
  onOpenChange,
  onConfirm,
  isPending,
  error,
}: {
  name: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isPending: boolean;
  error: string | null;
}) {
  function handleOpenChange(next: boolean) {
    if (!next && isPending) return;
    onOpenChange(next);
  }
  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-[60] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                data-testid="delete-dashboard-dialog"
              >
                <div className="flex flex-col space-y-3 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    Delete dashboard?
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {`"${name}" will be permanently deleted. This action cannot be undone.`}
                  </DialogPrimitive.Description>
                </div>
                {error && (
                  <p className="mt-4 text-[13px] text-destructive" role="alert">
                    {error}
                  </p>
                )}
                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    variant="outline"
                    className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                    style={{ border: "0.5px solid var(--hairline)" }}
                    onClick={() => handleOpenChange(false)}
                    disabled={isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="destructive"
                    className="rounded-2xl text-[13px] cursor-pointer"
                    onClick={onConfirm}
                    disabled={isPending}
                    data-testid="confirm-delete-dashboard-btn"
                  >
                    <Trash size={16} className="mr-1" />
                    {isPending ? "Deleting…" : "Delete"}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
